import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DataPackage, Schema } from '@apis/cas/generated';
import { environment } from '@environments/environment';
import { firstValueFrom, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ICasDataService } from './cas-data.interface';

@Injectable({
  providedIn: 'root',
})
export class CasOfflineDataService implements ICasDataService {
  private httpClient = inject(HttpClient);
  private manifestUrl = `${environment.offlineDataFolder}/offline-manifest.json`;

  async getSchema(): Promise<Schema> {
    const manifest = await this.getManifest();
    const schemaUrl = `${environment.offlineDataFolder}/${manifest.schema}`;
    return await firstValueFrom(
      this.httpClient.get<Schema>(schemaUrl).pipe(catchError(() => throwError(() => new Error('Failed to load schema')))),
    );
  }

  async getDataPackage(): Promise<DataPackage> {
    const manifest = await this.getManifest();
    const dataPackageUrl = `/${environment.offlineDataFolder}/${manifest.dataPackage}`;
    return await firstValueFrom(
      this.httpClient.get<DataPackage>(dataPackageUrl).pipe(catchError(() => throwError(() => new Error('Failed to load data package')))),
    );
  }

  async getDataPackageVersion(): Promise<string> {
    const dataPackage = await this.getDataPackage();
    if (dataPackage && dataPackage.versionUid) {
      return dataPackage.versionUid;
    } else {
      throw new Error('Data package version not found');
    }
  }

  private getManifest(): Promise<any> {
    // Cache bust so we always have the latest manifest
    const url = `${this.manifestUrl}?v=${Date.now()}`;
    return firstValueFrom(this.httpClient.get<any>(url).pipe(catchError(() => throwError(() => new Error('Failed to load manifest')))));
  }
}
